import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ClearIcon from "@mui/icons-material/Clear";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack,
    TextField, Tooltip, Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import StoryImageZoomDialog from "../story/StoryImageZoomDialog";
import AdminStoryService from "../story/AdminStoryService";
import AdminJigsawService from "./AdminJigsawService";
import AdminJigsawPackService from "./AdminJigsawPackService";
import JigsawPlaySummaryRow from "./JigsawPlaySummaryRow";
import { readPackTag, writePackTag } from "./jigsawPackTagStorage";

const PACK_LIMIT_BY_TIER = {
    free: 8,
    t1: 8,
    t2: 12,
};

function packSizeForTier(tier) {
    return PACK_LIMIT_BY_TIER[tier] || PACK_LIMIT_BY_TIER.t1;
}
const DEFAULT_SUGGEST_TAG = "art";
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;
const BROWSE_STORAGE_KEY = "admin.jigsaw.pack.detail.browse";

const PRODUCT_BY_TIER = {
    free: "com.pzl.jigsaw.muse.pack.tier.free",
    t1: "com.pzl.jigsaw.muse.pack.tier.1",
    t2: "com.pzl.jigsaw.muse.pack.tier.2",
};

const PACK_STATUSES = [
    { id: "DRAFT", label: "Draft" },
    { id: "READY", label: "Ready" },
    { id: "PUBLISHED", label: "Published" },
];

function packStatusOf(row) {
    const value = String(row?.status || "").trim().toUpperCase();
    if (PACK_STATUSES.some((item) => item.id === value)) return value;
    return row?.published === true ? "PUBLISHED" : "DRAFT";
}

const TIERS = [
    { id: "free", label: "Free" },
    { id: "t1", label: "T1 $0.99" },
    { id: "t2", label: "T2 $1.99" },
];

const ORDER_BY_OPTIONS = [
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
];

function storyTitle(story) {
    if (!story) return "";
    return story.english?.title || story.en?.title || story.french?.title || story.title || story.id || "";
}

function isDieLineImageUrl(url) {
    return String(url || "").includes("/jigsaw/");
}

function storyImage(story) {
    if (!story) return null;
    const candidates = [
        story.imageUrl,
        story.imageIcon400Url,
        story.imageIcon200Url,
        story.imagePreviewUrl,
    ];
    return candidates.find((url) => url && !isDieLineImageUrl(url)) || null;
}

function picturesFromPack(pack) {
    const pictures = [];
    const byStory = new Map();
    for (const board of pack?.boards || []) {
        const storyId = board.storyId;
        let picture = byStory.get(storyId);
        if (!picture) {
            picture = {
                storyId,
                title: board.title || storyId,
                imageUrl: board.imageUrl || null,
                free: board.free === true,
                state: board.state || "",
                jigsawIds: [],
            };
            byStory.set(storyId, picture);
            pictures.push(picture);
        }
        if (board.jigsawId) picture.jigsawIds.push(board.jigsawId);
    }
    return pictures;
}

function browseForPackTag(packTag) {
    const prefs = loadBrowsePrefs();
    if (!packTag) return prefs;
    return {
        ...prefs,
        language: "english",
        stateFilter: "PUBLISHED",
        packed: "false",
        tag: packTag,
        search: "",
        page: 0,
    };
}

function loadBrowsePrefs() {
    const defaults = {
        language: "english",
        stateFilter: "PUBLISHED",
        sort: "createdAt:desc",
        access: "",
        standalone: "",
        packed: "",
        tag: "",
        search: "",
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
    };
    try {
        const raw = localStorage.getItem(BROWSE_STORAGE_KEY);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return defaults;
        const language = parsed.language === "" || parsed.language === "english" || parsed.language === "french"
            ? parsed.language
            : defaults.language;
        const stateFilter = parsed.stateFilter === "" || parsed.stateFilter === "PUBLISHED"
            || parsed.stateFilter === "REVIEWED" || parsed.stateFilter === "DRAFT"
            ? parsed.stateFilter
            : defaults.stateFilter;
        const sort = ORDER_BY_OPTIONS.some((option) => option.value === parsed.sort)
            ? parsed.sort
            : defaults.sort;
        const access = parsed.access === "" || parsed.access === "true" || parsed.access === "false"
            ? parsed.access
            : "";
        const standalone = parsed.standalone === "" || parsed.standalone === "true" || parsed.standalone === "false"
            ? parsed.standalone
            : "";
        const packed = parsed.packed === "" || parsed.packed === "true" || parsed.packed === "false"
            ? parsed.packed
            : "";
        const tag = typeof parsed.tag === "string" ? parsed.tag : "";
        const search = typeof parsed.search === "string" ? parsed.search : "";
        const pageNum = parseInt(parsed.page, 10);
        const page = Number.isFinite(pageNum) && pageNum >= 0 ? pageNum : 0;
        const pageSizeNum = parseInt(parsed.pageSize, 10);
        const pageSize = PAGE_SIZE_OPTIONS.includes(pageSizeNum) ? pageSizeNum : DEFAULT_PAGE_SIZE;
        return { language, stateFilter, sort, access, standalone, packed, tag, search, page, pageSize };
    } catch {
        return defaults;
    }
}

function statusLabel(state) {
    const value = String(state || "").trim();
    if (!value) return "";
    return value.charAt(0) + value.slice(1).toLowerCase();
}

function PictureCard({ title, imageUrl, free, packed, state, checked, disabled, onToggle, onZoom, compact = false }) {
    const status = statusLabel(state);
    const accessLabel = packed ? "Packed" : free ? "Free" : free == null ? "" : "Paid";
    return (
        <Box>
            <Typography
                variant={compact ? "caption" : "body2"}
                noWrap
                title={title}
                sx={{ fontWeight: 600, lineHeight: 1.3 }}
            >
                {title}
            </Typography>
            {status ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {status}
                </Typography>
            ) : (
                <Box sx={{ mb: 0.5 }} />
            )}
            <Box
                onClick={disabled ? undefined : onToggle}
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: 2,
                    borderColor: checked ? "primary.main" : "divider",
                    bgcolor: "grey.900",
                    cursor: disabled ? "default" : "pointer",
                }}
            >
                {imageUrl ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={title}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="caption" color="grey.500">No image</Typography>
                    </Box>
                )}
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onClick={(event) => event.stopPropagation()}
                    onChange={onToggle}
                    sx={{
                        position: "absolute",
                        top: 6,
                        left: 6,
                        zIndex: 2,
                        p: 0.25,
                        bgcolor: "rgba(255,255,255,0.92)",
                        borderRadius: 0.5,
                        "&:hover": { bgcolor: "common.white" },
                    }}
                />
                {imageUrl && onZoom ? (
                    <Tooltip title="Zoom image">
                        <IconButton
                            size="small"
                            aria-label={`Zoom ${title}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                onZoom();
                            }}
                            sx={{
                                position: "absolute",
                                right: 4,
                                bottom: 4,
                                zIndex: 2,
                                width: 28,
                                height: 28,
                                p: 0,
                                bgcolor: "rgba(255,255,255,0.92)",
                                "&:hover": { bgcolor: "common.white" },
                            }}
                        >
                            <ZoomInIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {accessLabel ? (
                    <Chip
                        size="small"
                        label={accessLabel}
                        color={packed ? "warning" : free ? "success" : "default"}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            fontWeight: 700,
                            pointerEvents: "none",
                            ...(packed || free ? {} : { bgcolor: "grey.900", color: "common.white" }),
                        }}
                    />
                ) : null}
            </Box>
        </Box>
    );
}

export default function AdminJigsawPackDetailPage() {
    const { packId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const packTag = String(location.state?.tag || readPackTag(packId) || "").trim();
    const [pack, setPack] = useState(null);
    const [pictures, setPictures] = useState([]);
    const [title, setTitle] = useState("");
    const [productId, setProductId] = useState("");
    const [tier, setTier] = useState("t2");
    const [sortOrder, setSortOrder] = useState("0");
    const [memo, setMemo] = useState("");
    const [packStatus, setPackStatus] = useState("DRAFT");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [suggestingTitle, setSuggestingTitle] = useState(false);
    const [savedBrowse] = useState(() => browseForPackTag(packTag));
    const [language, setLanguage] = useState(savedBrowse.language);
    const [stateFilter, setStateFilter] = useState(savedBrowse.stateFilter);
    const [sort, setSort] = useState(savedBrowse.sort);
    const [access, setAccess] = useState(savedBrowse.access);
    const [standalone, setStandalone] = useState(savedBrowse.standalone);
    const [packedFilter, setPackedFilter] = useState(savedBrowse.packed);
    const [tag, setTag] = useState(savedBrowse.tag);
    const [tagQuery, setTagQuery] = useState(savedBrowse.tag.trim());
    const [search, setSearch] = useState(savedBrowse.search);
    const [keyword, setKeyword] = useState(savedBrowse.search.trim());
    const [page, setPage] = useState(savedBrowse.page);
    const [pageSize, setPageSize] = useState(savedBrowse.pageSize);
    const [totalPages, setTotalPages] = useState(1);
    const [browsing, setBrowsing] = useState(false);
    const [hits, setHits] = useState([]);
    const [pendingGroup, setPendingGroup] = useState(null);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestTag, setSuggestTag] = useState("");
    const [suggestKeyword, setSuggestKeyword] = useState("");
    const [suggesting, setSuggesting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [zoomImage, setZoomImage] = useState(null);
    const storyArtCache = useRef(new Map());
    const [regenDialogOpen, setRegenDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [savingFreeStoryId, setSavingFreeStoryId] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const packSize = packSizeForTier(tier);

    const load = useCallback(async () => {
        setLoading(true);
        const result = await AdminJigsawPackService.get(packId);
        setLoading(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Pack not found" });
            setPack(null);
            return;
        }
        const row = result.data;
        setPack(row);
        setTitle(row.title || "");
        const nextTier = PACK_LIMIT_BY_TIER[row.tier] ? row.tier : "t2";
        setTier(nextTier);
        setProductId(PRODUCT_BY_TIER[nextTier]);
        setSortOrder(row.sortOrder == null ? "0" : String(row.sortOrder));
        setMemo(row.memo == null ? "" : String(row.memo));
        setPackStatus(packStatusOf(row));
        setPictures(picturesFromPack(row));
    }, [packId]);

    const loadBrowse = useCallback(async () => {
        setBrowsing(true);
        const freeParam = access === "true" ? true : access === "false" ? false : undefined;
        const packedParam = packedFilter === "true" ? true : packedFilter === "false" ? false : undefined;
        const standaloneParam = standalone === "true" ? true : standalone === "false" ? false : undefined;
        const tags = tagQuery.trim() ? [tagQuery.trim()] : undefined;
        const result = await AdminJigsawService.list(
            page,
            pageSize,
            sort,
            stateFilter || undefined,
            keyword.trim(),
            language || undefined,
            tags,
            freeParam,
            standaloneParam,
            packedParam,
        );
        setBrowsing(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Could not load pictures" });
            return;
        }
        const pages = Math.max(1, result.data?.totalPages || 1);
        setTotalPages(pages);
        if (page >= pages) {
            setPage(pages - 1);
            return;
        }
        setHits(result.data?.content || []);
    }, [page, pageSize, sort, stateFilter, keyword, language, tagQuery, access, standalone, packedFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadBrowse(); }, [loadBrowse]);

    useEffect(() => {
        try {
            localStorage.setItem(BROWSE_STORAGE_KEY, JSON.stringify({
                language,
                stateFilter,
                sort,
                access,
                standalone,
                packed: packedFilter,
                tag,
                search,
                page,
                pageSize,
            }));
        } catch {
        }
    }, [language, stateFilter, sort, access, standalone, packedFilter, tag, search, page, pageSize]);

    const saveMeta = async () => {
        setSaving(true);
        const result = await AdminJigsawPackService.update(packId, {
            title: title.trim(),
            productId: productId.trim(),
            tier,
            sortOrder: Number(sortOrder) || 0,
            memo,
            status: packStatus,
            published: packStatus === "PUBLISHED",
        });
        setSaving(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Save failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Pack saved" });
        setPack(result.data);
        setPackStatus(packStatusOf(result.data));
        if (result.data && Object.prototype.hasOwnProperty.call(result.data, "memo")) {
            setMemo(result.data.memo == null ? "" : String(result.data.memo));
        }
        setPictures(picturesFromPack(result.data));
        loadBrowse();
    };

    const suggestTitle = async () => {
        setSuggestingTitle(true);
        const result = await AdminJigsawPackService.suggestTitle(packId);
        setSuggestingTitle(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Could not suggest a title" });
            return;
        }
        setTitle(result.data);
        setSnackbar({ open: true, severity: "success", message: "Title suggested. Save to keep it." });
    };

    const savePictures = async (nextPictures) => {
        const jigsawIds = nextPictures.flatMap((row) => row.jigsawIds || []);
        setSaving(true);
        const result = await AdminJigsawPackService.update(packId, { jigsawIds });
        setSaving(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Could not update pictures" });
            return;
        }
        setPack(result.data);
        setPictures(picturesFromPack(result.data));
        loadBrowse();
    };

    const addPicture = (group) => {
        const story = group?.story;
        const storyId = story?.id;
        const boards = Array.isArray(group?.templates) ? group.templates : [];
        const jigsawIds = boards.map((board) => board?.id).filter(Boolean);
        if (!storyId || jigsawIds.length === 0) {
            setSnackbar({ open: true, severity: "warning", message: "That picture has no jigsaw boards" });
            return;
        }
        if (pictures.some((row) => row.storyId === storyId)) return;
        if (pictures.length >= packSize) {
            setSnackbar({ open: true, severity: "warning", message: `A pack holds ${packSize} pictures` });
            return;
        }
        savePictures([...pictures, {
            storyId,
            title: storyTitle(story) || storyId,
            imageUrl: storyImage(story),
            free: group?.storyJigsaw?.free === true,
            state: group?.storyJigsaw?.state || "",
            jigsawIds,
        }]);
    };

    const removePicture = (storyId) => {
        savePictures(pictures.filter((row) => row.storyId !== storyId));
    };

    const needsPackAdjust = (group) => {
        const free = group?.storyJigsaw?.free === true;
        const state = String(group?.storyJigsaw?.state || "").toUpperCase();
        return free || state !== "PUBLISHED";
    };

    const toggleHit = (group) => {
        const storyId = group?.story?.id;
        if (!storyId) return;
        if (pictures.some((row) => row.storyId === storyId)) {
            removePicture(storyId);
            return;
        }
        if (needsPackAdjust(group)) {
            setPendingGroup(group);
            return;
        }
        addPicture(group);
    };

    const confirmPending = () => {
        const group = pendingGroup;
        setPendingGroup(null);
        if (group) addPicture(group);
    };

    const applySearch = () => {
        const nextTag = tag.trim();
        setPage(0);
        setKeyword(search.trim());
        setTagQuery(nextTag);
        if (nextTag) writePackTag(packId, nextTag);
    };

    const openSuggest = () => {
        setSuggestTag(DEFAULT_SUGGEST_TAG);
        setSuggestKeyword("");
        setSuggestOpen(true);
    };

    const performSuggest = async () => {
        const room = packSize - pictures.length;
        if (room <= 0) {
            setSnackbar({ open: true, severity: "warning", message: `A pack holds ${packSize} pictures` });
            setSuggestOpen(false);
            return;
        }
        setSuggesting(true);
        const result = await AdminJigsawPackService.suggestPictures(packId, {
            tag: suggestTag.trim(),
            keyword: suggestKeyword.trim(),
            language: language || "",
        });
        if (!result.success) {
            setSuggesting(false);
            setSnackbar({ open: true, severity: "error", message: result.error || "Could not suggest pictures" });
            return;
        }
        const hits = Array.isArray(result.data) ? result.data : [];
        const next = [...pictures];
        hits.forEach((row) => {
            if (next.length >= packSize) return;
            if (!row?.storyId || !(row.jigsawIds || []).length) return;
            if (next.some((item) => item.storyId === row.storyId)) return;
            next.push({
                storyId: row.storyId,
                title: row.title || row.storyId,
                imageUrl: row.imageUrl || null,
                free: row.free === true,
                state: row.state || "",
                jigsawIds: row.jigsawIds,
            });
        });
        setSuggesting(false);
        setSuggestOpen(false);
        if (next.length === pictures.length) {
            setSnackbar({ open: true, severity: "info", message: "No similar pictures found" });
            return;
        }
        savePictures(next);
    };

    const publishPack = async () => {
        if (pictures.length < packSize) {
            setSnackbar({ open: true, severity: "warning", message: `Check ${packSize} pictures before publishing` });
            return;
        }
        setSaving(true);
        const result = await AdminJigsawPackService.update(packId, {
            title: title.trim(),
            productId: productId.trim(),
            tier,
            sortOrder: Number(sortOrder) || 0,
            memo,
            status: "PUBLISHED",
            published: true,
        });
        setSaving(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Publish failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Pack published" });
        setPackStatus("PUBLISHED");
        setPack(result.data);
        setPictures(picturesFromPack(result.data));
        loadBrowse();
    };

    useEffect(() => {
        let cancelled = false;
        const applyArt = (prev) => prev.map((row) => {
            const art = storyArtCache.current.get(row.storyId);
            if (art && isDieLineImageUrl(row.imageUrl)) return { ...row, imageUrl: art };
            return row;
        });
        if (pictures.some((row) => isDieLineImageUrl(row.imageUrl) && storyArtCache.current.get(row.storyId))) {
            setPictures(applyArt);
            setZoomImage((prev) => {
                const art = prev?.storyId ? storyArtCache.current.get(prev.storyId) : null;
                return art && isDieLineImageUrl(prev.url) ? { ...prev, url: art } : prev;
            });
        }
        const needed = pictures.filter((row) => (
            row.storyId && isDieLineImageUrl(row.imageUrl) && !storyArtCache.current.has(row.storyId)
        ));
        if (needed.length === 0) return undefined;
        needed.forEach((row) => storyArtCache.current.set(row.storyId, null));
        (async () => {
            await Promise.all(needed.map(async (row) => {
                const result = await AdminStoryService.getById(row.storyId);
                const art = result.success ? storyImage(result.data) : null;
                if (art) storyArtCache.current.set(row.storyId, art);
            }));
            if (cancelled) return;
            setPictures(applyArt);
            setZoomImage((prev) => {
                const art = prev?.storyId ? storyArtCache.current.get(prev.storyId) : null;
                return art && isDieLineImageUrl(prev.url) ? { ...prev, url: art } : prev;
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [pictures]);

    const openZoom = (image) => {
        if (!image?.url) return;
        setZoomImage(image);
    };

    const handleStoryFreeChange = async (storyId, free) => {
        setSavingFreeStoryId(storyId);
        const result = await AdminJigsawService.setStoryFree(storyId, free);
        setSavingFreeStoryId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update free flag" });
            return;
        }
        setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, free } : prev));
        setPictures((prev) => prev.map((row) => (row.storyId === storyId ? { ...row, free } : row)));
        setHits((prev) => prev.map((group) => (
            group?.story?.id === storyId
                ? { ...group, storyJigsaw: { ...group.storyJigsaw, free } }
                : group
        )));
    };

    const handleConfirmRegenImage = async (storyId) => {
        if (!storyId || regenLoading) return;
        setRegenLoading(true);
        try {
            const result = await AdminStoryService.regenImage(storyId);
            if (!result.success) {
                setSnackbar({ open: true, severity: "error", message: result.error || "Regenerate image failed" });
                return;
            }
            const nextUrl = storyImage(result.data) || zoomImage?.url || "";
            const busted = nextUrl
                ? `${nextUrl}${nextUrl.includes("?") ? "&" : "?"}v=${Date.now()}`
                : "";
            if (busted) {
                setZoomImage((prev) => (prev ? { ...prev, url: busted } : prev));
                setPictures((prev) => prev.map((row) => (
                    row.storyId === storyId ? { ...row, imageUrl: busted } : row
                )));
            }
            const preview = await AdminJigsawService.genPreview(storyId);
            setSnackbar({
                open: true,
                severity: preview.success ? "success" : "warning",
                message: preview.success
                    ? "Image replaced. Jigsaw puzzle images were regenerated."
                    : "Image replaced. Jigsaw preview refresh failed — use Gen preview if needed.",
            });
            loadBrowse();
        } catch (err) {
            setSnackbar({ open: true, severity: "error", message: err?.message || "Regenerate image failed" });
        } finally {
            setRegenLoading(false);
        }
    };

    const handlePublishStory = async () => {
        const storyId = zoomImage?.storyId;
        const templates = zoomImage?.templates;
        if (!storyId || publishing) return;
        if (Array.isArray(templates) && templates.length === 0) {
            setSnackbar({ open: true, severity: "error", message: "No jigsaw templates to publish" });
            return;
        }
        setPublishing(true);
        const result = await AdminJigsawService.publishStory(storyId);
        setPublishing(false);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Publish failed" });
            return;
        }
        setZoomImage((prev) => (prev?.storyId === storyId ? { ...prev, state: "PUBLISHED" } : prev));
        setPictures((prev) => prev.map((row) => (
            row.storyId === storyId ? { ...row, state: "PUBLISHED" } : row
        )));
        setHits((prev) => prev.map((group) => (
            group?.story?.id === storyId
                ? { ...group, storyJigsaw: { ...group.storyJigsaw, state: "PUBLISHED" } }
                : group
        )));
        setSnackbar({ open: true, severity: "success", message: "Published successfully" });
    };

    const handleConfirmDeleteStory = async (storyId) => {
        if (!storyId || deleting) return;
        setDeleting(true);
        try {
            const result = await AdminJigsawService.removeByStoryId(storyId);
            if (!result.success) {
                setSnackbar({ open: true, severity: "error", message: result.error || "Delete failed" });
                return;
            }
            setZoomImage(null);
            setSnackbar({ open: true, severity: "success", message: "Jigsaw deleted" });
            const packResult = await AdminJigsawPackService.get(packId);
            if (packResult.success) {
                setPack(packResult.data);
                setPictures(picturesFromPack(packResult.data));
            }
            loadBrowse();
        } catch (err) {
            setSnackbar({ open: true, severity: "error", message: err?.message || "Delete failed" });
        } finally {
            setDeleting(false);
        }
    };

    const handleConfirmDisableStory = async (storyId) => {
        if (!storyId || disabling) return;
        setDisabling(true);
        try {
            const result = await AdminJigsawService.disableStory(storyId);
            if (!result.success) {
                setSnackbar({ open: true, severity: "error", message: result.error || "Disable failed" });
                return;
            }
            setZoomImage((prev) => (prev ? { ...prev, state: "DISABLED" } : prev));
            setPictures((prev) => prev.map((row) => (
                row.storyId === storyId ? { ...row, state: "DISABLED" } : row
            )));
            setHits((prev) => prev.map((group) => (
                group?.story?.id === storyId
                    ? { ...group, storyJigsaw: { ...group.storyJigsaw, state: "DISABLED" } }
                    : group
            )));
            setSnackbar({
                open: true,
                severity: "success",
                message: "Puzzle disabled. Players cannot access it. It will be discarded in 1 week.",
            });
            loadBrowse();
        } catch (err) {
            setSnackbar({ open: true, severity: "error", message: err?.message || "Disable failed" });
        } finally {
            setDisabling(false);
        }
    };

    const deletePack = async (id) => {
        const result = await AdminJigsawPackService.remove(id);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Delete failed" });
            return;
        }
        navigate("/admin/jigsaw/packs");
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!pack) {
        return (
            <Box sx={{ p: 2 }}>
                <Button component={RouterLink} to="/admin/jigsaw/packs">Back to packs</Button>
                <CustomSnackbar
                    open={snackbar.open}
                    setOpen={(next) => setSnackbar((prev) => ({ ...prev, open: next }))}
                    severity={snackbar.severity}
                    message={snackbar.message}
                />
            </Box>
        );
    }

    const selectedIds = new Set(pictures.map((row) => row.storyId));

    return (
        <Box sx={{ p: 2 }}>
            <Button
                component={RouterLink}
                to="/admin/jigsaw/packs"
                size="small"
                sx={{ mb: 1 }}
            >
                Back to packs
            </Button>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                        <Stack spacing={2}>
                            <TextField
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="Suggest a pack title"
                                                    onClick={suggestTitle}
                                                    disabled={suggestingTitle || saving || pictures.length === 0}
                                                    color="primary"
                                                    edge="end"
                                                >
                                                    {suggestingTitle ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField label="Product id" value={PRODUCT_BY_TIER[tier] || productId} InputProps={{ readOnly: true }} />
                            <TextField select label="Tier" value={tier} onChange={(e) => {
                                const nextTier = e.target.value;
                                setTier(nextTier);
                                setProductId(PRODUCT_BY_TIER[nextTier] || "");
                            }}>
                                {TIERS.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Sort" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
                            <TextField
                                label="Memo"
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                multiline
                                minRows={3}
                                placeholder="Notes for this pack"
                            />
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                <Button variant="outlined" onClick={saveMeta} disabled={saving}>Save</Button>
                                <FormControl size="small" sx={{ minWidth: 148 }}>
                                    <InputLabel id="pack-status-label">Status</InputLabel>
                                    <Select
                                        labelId="pack-status-label"
                                        label="Status"
                                        value={packStatus}
                                        onChange={(e) => setPackStatus(e.target.value)}
                                    >
                                        {PACK_STATUSES.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    onClick={publishPack}
                                    disabled={saving || pictures.length < packSize}
                                >
                                    Publish
                                </Button>
                                <Button
                                    color="error"
                                    variant="outlined"
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={saving}
                                >
                                    Delete
                                </Button>
                            </Stack>
                            {pictures.length < packSize ? (
                                <Typography variant="caption" color="text.secondary">
                                    Check {packSize} similar pictures, then publish.
                                </Typography>
                            ) : null}
                            <Button
                                variant="outlined"
                                onClick={openSuggest}
                                disabled={suggesting || saving || pictures.length >= packSize}
                            >
                                {`Suggest ${packSize} similar`}
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {pictures.length} / {packSize} pictures
                        </Typography>
                        <Box sx={{ overflowY: "auto", maxHeight: 560, pr: 0.5 }}>
                            {pictures.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No pictures selected yet.
                                </Typography>
                            ) : (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {pictures.map((picture) => (
                                        <Box key={picture.storyId} sx={{ width: 192, flexShrink: 0 }}>
                                            <PictureCard
                                                compact
                                                title={picture.title}
                                                imageUrl={picture.imageUrl}
                                free={false}
                                packed
                                state={picture.state}
                                                checked
                                                disabled={saving}
                                                onToggle={() => removePicture(picture.storyId)}
                                                onZoom={() => openZoom({
                                                    url: picture.imageUrl,
                                                    title: picture.title,
                                                    storyId: picture.storyId,
                                                    state: picture.state,
                                                    free: picture.free === true,
                                                    standalone: picture.standalone === true,
                                                    templates: picture.jigsawIds,
                                                })}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 1 }}>
                {tagQuery ? `Pictures in ${tagQuery}` : "Pictures"}
            </Typography>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Language</InputLabel>
                    <Select value={language} label="Language" onChange={(e) => { setLanguage(e.target.value); setPage(0); }}>
                        <MenuItem value="">All languages</MenuItem>
                        <MenuItem value="english">English</MenuItem>
                        <MenuItem value="french">French</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>State</InputLabel>
                    <Select value={stateFilter} label="State" onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="PUBLISHED">Published</MenuItem>
                        <MenuItem value="REVIEWED">Reviewed</MenuItem>
                        <MenuItem value="DRAFT">Draft</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Order by</InputLabel>
                    <Select value={sort} label="Order by" onChange={(e) => { setSort(e.target.value); setPage(0); }}>
                        {ORDER_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Access</InputLabel>
                    <Select value={access} label="Access" onChange={(e) => { setAccess(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Free</MenuItem>
                        <MenuItem value="false">Paid</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Standalone</InputLabel>
                    <Select value={standalone} label="Standalone" onChange={(e) => { setStandalone(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Standalone</MenuItem>
                        <MenuItem value="false">Not standalone</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Packed</InputLabel>
                    <Select value={packedFilter} label="Packed" onChange={(e) => { setPackedFilter(e.target.value); setPage(0); }}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Packed</MenuItem>
                        <MenuItem value="false">Exclude packed</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Tag"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
                    sx={{ minWidth: 140 }}
                />
                <TextField
                    size="small"
                    label="Search"
                    placeholder="Search by title"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
                    sx={{ minWidth: 200 }}
                />
                <Button variant="outlined" onClick={applySearch}>Search</Button>
            </Stack>

            {browsing ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : hits.length === 0 ? (
                <Paper sx={{ py: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">No jigsaw pictures found.</Typography>
                </Paper>
            ) : (
                <Grid container spacing={1.5}>
                    {hits.map((group) => {
                        const story = group.story;
                        const storyId = story?.id;
                        const checked = selectedIds.has(storyId);
                        return (
                            <Grid key={storyId} size={{ xs: 6, sm: 4, md: 3 }}>
                                <PictureCard
                                    title={storyTitle(story) || storyId}
                                    imageUrl={storyImage(story)}
                                    free={group.storyJigsaw?.free === true}
                                    packed={group.storyJigsaw?.packed === true}
                                    state={group.storyJigsaw?.state}
                                    checked={checked}
                                    disabled={saving}
                                    onToggle={() => toggleHit(group)}
                                    onZoom={() => openZoom({
                                        url: storyImage(story),
                                        title: storyTitle(story) || storyId,
                                        storyId,
                                        state: group.storyJigsaw?.state,
                                        free: group.storyJigsaw?.free === true,
                                        standalone: Boolean(story?.standaloneImage),
                                        templates: group.templates,
                                    })}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="jigsaw-pack-page-size-label">Page size</InputLabel>
                    <Select
                        labelId="jigsaw-pack-page-size-label"
                        label="Page size"
                        value={pageSize}
                        onChange={(event) => {
                            setPageSize(Number(event.target.value));
                            setPage(0);
                        }}
                    >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button size="small" disabled={page <= 0 || browsing} onClick={() => setPage((current) => current - 1)}>
                    Previous
                </Button>
                <Typography variant="body2">{page + 1} / {totalPages}</Typography>
                <Button
                    size="small"
                    disabled={browsing || page + 1 >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                >
                    Next
                </Button>
            </Stack>

            <Dialog open={suggestOpen} onClose={() => !suggesting && setSuggestOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{`Suggest ${packSize} similar`}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography>
                            {pictures.length === 0
                                ? `Find ${packSize} similar pictures and add them to this pack?`
                                : `This pack already has ${pictures.length} pictures. Find ${packSize - pictures.length} more that match those pictures?`}
                        </Typography>
                        <TextField
                            label="Tag"
                            value={suggestTag}
                            onChange={(event) => setSuggestTag(event.target.value)}
                            helperText="Defaults to art. Clear it to search every picture. A keyword can be used with the tag or on its own."
                            disabled={suggesting}
                            slotProps={{
                                input: {
                                    endAdornment: suggestTag ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Clear tag"
                                                edge="end"
                                                size="small"
                                                disabled={suggesting}
                                                onClick={() => setSuggestTag("")}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                },
                            }}
                        />
                        <TextField
                            label="Keyword"
                            value={suggestKeyword}
                            onChange={(event) => setSuggestKeyword(event.target.value)}
                            helperText="Optional. Works with the tag, or alone when the tag is cleared."
                            disabled={suggesting}
                        />
                        <Typography variant="body2" color="text.secondary">
                            New pictures are chosen to match the subject of the pictures already in this pack.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuggestOpen(false)} disabled={suggesting}>Cancel</Button>
                    <Button variant="contained" onClick={performSuggest} disabled={suggesting || saving}>
                        {suggesting ? "Looking" : "Perform"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(pendingGroup)} onClose={() => setPendingGroup(null)}>
                <DialogTitle>Add to pack</DialogTitle>
                <DialogContent>
                    <Typography>
                        Change the paid option, and change the status to published too.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingGroup(null)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmPending} disabled={saving}>
                        Change and add
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={regenDialogOpen}
                setOpen={setRegenDialogOpen}
                title="Regenerate image?"
                message="This replaces the current illustration and regenerates jigsaw puzzle images. Story text is not changed."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmRegenImage}
                warningAlert="Would you like to re-generate this image? Did you check the players who played this puzzle."
                okText="OK"
                cancelText="Cancel"
            />
            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                title="Delete jigsaw"
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                message={[
                    "This permanently deletes the story record, the story image, all jigsaw templates, and the related sliding puzzle.",
                    "Facebook and Instagram posts of this image are removed when post ids are known.",
                    "The crossword or word-search puzzle is not deleted. Scheduled jobs will not recreate this story or its sliding/jigsaw puzzles.",
                    "This action cannot be undone.",
                ]}
                callback={handleConfirmDeleteStory}
            />
            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable this puzzle?"
                message="Players will not be able to open or continue this puzzle. It stays disabled for 1 week, then it is discarded."
                targetName={
                    zoomImage?.title
                        ? `'${zoomImage.title}'`
                        : zoomImage?.storyId
                          ? `Story ${zoomImage.storyId}`
                          : "This jigsaw"
                }
                targetId={zoomImage?.storyId}
                callback={handleConfirmDisableStory}
                warningAlert="Not available. Will be removed in 1 week."
                okText="Disable"
                cancelText="Cancel"
            />
            <StoryImageZoomDialog
                open={Boolean(zoomImage?.url)}
                imageUrl={zoomImage?.url || ""}
                title={zoomImage?.title}
                onClose={() => !regenLoading && !deleting && !disabling && !publishing && setZoomImage(null)}
                onDelete={zoomImage?.storyId ? () => setDeleteDialogOpen(true) : undefined}
                deleting={deleting}
                onDisable={
                    zoomImage?.storyId && String(zoomImage?.state || "").toUpperCase() === "PUBLISHED"
                        ? () => setDisableDialogOpen(true)
                        : undefined
                }
                disabling={disabling}
                onRegen={zoomImage?.storyId ? () => setRegenDialogOpen(true) : undefined}
                regenLoading={regenLoading}
                free={zoomImage?.free === true}
                onFreeChange={
                    zoomImage?.storyId
                        ? (checked) => handleStoryFreeChange(zoomImage.storyId, checked)
                        : undefined
                }
                savingFree={savingFreeStoryId === zoomImage?.storyId}
                state={zoomImage?.state}
                standalone={zoomImage?.standalone === true}
                allowPublishDisabled
                onPublish={zoomImage?.storyId ? handlePublishStory : undefined}
                publishing={publishing}
                extra={
                    zoomImage?.storyId ? (
                        <JigsawPlaySummaryRow storyId={zoomImage.storyId} />
                    ) : null
                }
            />
            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Delete pack"
                message="Delete this pack? Pictures return to the jigsaw list. Packs that already have purchases cannot be deleted."
                targetName={title || pack.title}
                targetId={packId}
                callback={deletePack}
            />
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(next) => setSnackbar((prev) => ({ ...prev, open: next }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
